import React, { createContext, useContext, useState, useMemo, useCallback, useEffect, ReactNode } from 'react';
import PocketBase from 'pocketbase';
import { pb, pbUrl } from '../../../shared/api/pocketbase';
import { User, UserRole } from '../../../entities/user/model/types';
import { logAction } from '../../../shared/lib/userLogger';

type PbRecord = Record<string, unknown>;

interface AuthContextValue {
  currentUser: User | null;
  isAuthenticated: boolean;
  isReady: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  impersonateUser: (userId: string, superEmail: string, superPassword: string) => Promise<void>;
}

const normalizeRole = (role: unknown): UserRole => {
  const r = String(role ?? '').toUpperCase();
  return r === 'ADMIN' || r === 'MANAGER' ? r : 'USER';
};

const mapUser = (record: PbRecord): User => ({
  id: record.id as string,
  email: record.email as string,
  name: (record.name as string) || (record.email as string),
  phone: (record.phone as string) || undefined,
  role: normalizeRole(record.role),
  created: (record.created as string) ?? '',
  needResetPass: Boolean(record.needResetPass),
  isBlocked: Boolean(record.isBlocked),
});

const getStoredUser = (): User | null =>
  pb.authStore.isValid && pb.authStore.model
    ? mapUser(pb.authStore.model as unknown as PbRecord)
    : null;

const doAuthRefresh = async (): Promise<User> => {
  const { record } = await pb.collection('users').authRefresh();
  return mapUser(record as unknown as PbRecord);
};

const clearIfBlocked = (user: User): User | null => {
  if (user.isBlocked) {
    pb.authStore.clear();
    return null;
  }
  return user;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(getStoredUser);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!pb.authStore.isValid || !pb.authStore.model) {
      setIsReady(true);
      return;
    }

    doAuthRefresh()
      .then(user => setCurrentUser(clearIfBlocked(user)))
      .catch(() => {
        pb.authStore.clear();
        setCurrentUser(null);
      })
      .finally(() => setIsReady(true));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { record } = await pb.collection('users').authWithPassword(email, password);
    const user = mapUser(record as unknown as PbRecord);
    if (user.isBlocked) {
      pb.authStore.clear();
      throw new Error('Ваш аккаунт заблокирован');
    }
    setCurrentUser(user);
    logAction('Logged in', 'NONE');
  }, []);

  const logout = useCallback(() => {
    logAction('Logged out', 'LOGOUT');
    pb.authStore.clear();
    setCurrentUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    setCurrentUser(clearIfBlocked(await doAuthRefresh()));
  }, []);

  const impersonateUser = useCallback(async (userId: string, superEmail: string, superPassword: string) => {
    const tempPb = new PocketBase(pbUrl);
    await tempPb.collection('_superusers').authWithPassword(superEmail, superPassword);
    const client = await tempPb.collection('users').impersonate(userId, 3600);
    const record = client.authStore.record as unknown as PbRecord;
    // @ts-expect-error — PocketBase types don't expose save() publicly
    pb.authStore.save(client.authStore.token, record);
    setCurrentUser(mapUser(record));
    logAction(`Impersonated user ${userId}`, 'NONE');
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    currentUser,
    isAuthenticated: currentUser !== null,
    isReady,
    login,
    logout,
    refreshUser,
    impersonateUser,
  }), [currentUser, isReady, login, logout, refreshUser, impersonateUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
